import { useMemo } from 'react';
import { useGlobalLoading } from '@/stores/loadingStore';
import type { Application, JobPosition, JobPosting } from '@/types';
import { mergeApplicationsForAnalytics } from '@/lib/applicationArchive';
import {
  useApplicationsQuery,
  useArchivedApplicationsForAnalyticsQuery,
  usePositionsQuery,
  usePostingsAllQuery,
} from '@/hooks/useRecruiterListQueries';

export interface UseDashboardDataReturn {
  positions: JobPosition[];
  postings: JobPosting[];
  applications: Application[];
  loading: boolean;
  error: string | null;
}

export function useDashboardData(companyId: string | undefined): UseDashboardDataReturn {
  const positionsQuery = usePositionsQuery(companyId);
  const postingsQuery = usePostingsAllQuery(companyId);
  const activeApplicationsQuery = useApplicationsQuery(companyId, false);
  const archivedApplicationsQuery = useArchivedApplicationsForAnalyticsQuery(companyId);

  const applications = useMemo(
    () => mergeApplicationsForAnalytics(
      activeApplicationsQuery.data ?? [],
      archivedApplicationsQuery.data ?? [],
    ),
    [activeApplicationsQuery.data, archivedApplicationsQuery.data],
  );

  const loading = !!companyId && (
    positionsQuery.isLoading
    || postingsQuery.isLoading
    || activeApplicationsQuery.isLoading
    || archivedApplicationsQuery.isLoading
  );

  const error = useMemo(() => {
    const err = positionsQuery.error
      ?? postingsQuery.error
      ?? activeApplicationsQuery.error;
    if (!err) return null;
    return err instanceof Error ? err.message : 'Failed to load dashboard data';
  }, [
    positionsQuery.error,
    postingsQuery.error,
    activeApplicationsQuery.error,
  ]);

  useGlobalLoading(loading, 'dashboard');

  return {
    positions: positionsQuery.data ?? [],
    postings: postingsQuery.data ?? [],
    applications,
    loading,
    error,
  };
}
